import type { FC } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Iconify,
  LoadingPromiseButtonAdvanced,
  Modal,
  ModalInner,
} from '@pflegenavi/web-components';
import {
  Button,
  Card,
  IconButton,
  Stack,
  styled,
  Typography,
} from '@mui/material';

interface ServiceProviderSubmitReceiptBatchModalProps {
  open: boolean;
  submitDisabled: boolean;
  handleClose: () => void;
  handleSubmit: () => Promise<void>;
  isSubmitting: boolean;
  numberOfReceipts: number;
}

export const ServiceProviderSubmitReceiptBatchModal: FC<
  ServiceProviderSubmitReceiptBatchModalProps
> = ({
  open,
  submitDisabled,
  handleClose,
  handleSubmit,
  isSubmitting,
  numberOfReceipts,
}) => {
  const { t } = useTranslation();

  const onClose = (_: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick' && !isSubmitting) {
      handleClose();
    }
  };

  return (
    <Modal open={open} handleClose={onClose}>
      <ModalInner>
        <ModalCard>
          <Stack
            sx={{
              gap: 3,
              flex: 1,
            }}
          >
            <Stack
              direction="row"
              style={{ marginBottom: -10 }}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                {t('sp-web-app.receipt-batch.submit-title')}
              </Typography>
              <IconButton
                onClick={() => {
                  if (handleClose) {
                    handleClose();
                  }
                }}
                sx={{
                  padding: '16px',
                  marginRight: '-8px',
                }}
                disabled={isSubmitting}
              >
                <Iconify
                  icon={'eva:close-fill'}
                  sx={{
                    position: 'absolute',
                  }}
                  width={20}
                />
              </IconButton>
            </Stack>

            <Stack
              sx={{
                gap: 1,
              }}
            >
              {numberOfReceipts < 5 ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('confirmSubmissionReceiptsBatchText', {
                      count: numberOfReceipts,
                    })}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('confirmSubmissionReceiptsBatchTextDescription')}
                  </Typography>
                </>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('sp-web-app.receipt-batch.submit-description')}
                </Typography>
              )}
            </Stack>

            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.preventDefault();
                  if (handleClose) {
                    handleClose();
                  }
                }}
              >
                {t('actions.cancel')}
              </Button>
              <LoadingPromiseButtonAdvanced
                variant="contained"
                color="primary"
                disabled={submitDisabled}
                onClick={async (e) => {
                  e.preventDefault();
                  await handleSubmit();
                  handleClose();
                }}
              >
                {t('sp-web-app.receipt-batch.submit')}
              </LoadingPromiseButtonAdvanced>
            </Stack>
          </Stack>
        </ModalCard>
      </ModalInner>
    </Modal>
  );
};

const ModalCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    width: 480,
    maxWidth: '100%',
    userSelect: 'none',
  })
);
